import {
  onCleanup,
  on,
  createEffect,
  createMemo,
  createRoot,
  createSignal,
  observable,
} from 'https://cdn.jsdelivr.net/npm/solid-js@1.7.3/+esm';
import { insert } from 'https://cdn.jsdelivr.net/npm/solid-js@1.7.3/web/+esm';
import { register, compose } from 'https://cdn.jsdelivr.net/npm/component-register@0.6.7/+esm';

import BlazeSlider from 'https://cdn.jsdelivr.net/npm/blaze-slider@1.9.3/+esm';

// Utility custom element functions

function createProps(raw) {
  const keys = Object.keys(raw);
  const props = {};
  for (let i = 0; i < keys.length; i++) {
    const [get, set] = createSignal(raw[keys[i]]);
    Object.defineProperty(props, keys[i], {
      get() {
        return get();
      },
      set(v) {
        set(() => v);
      },
    });
  }
  return props;
}

function lookupContext(el) {
  if (el.assignedSlot && el.assignedSlot._$owner) return el.assignedSlot._$owner;
  let next = el.parentNode;
  while (next && !next._$owner && !(next.assignedSlot && next.assignedSlot._$owner)) next = next.parentNode;
  return next && next.assignedSlot ? next.assignedSlot._$owner : el._$owner;
}

function withSolid(ComponentType) {
  return (rawProps, options) => {
    const { element } = options;
    return createRoot((dispose) => {
      const props = createProps(rawProps);
      element.addPropertyChangedCallback((key, val) => (props[key] = val));
      element.addReleaseCallback(() => {
        element.renderRoot.textContent = '';
        dispose();
      });
      const comp = ComponentType(props, options);
      return insert(element.renderRoot, comp);
    }, lookupContext(element));
  };
}

function withNoShadowDOM(ComponentType = (props, options) => {}) {
  return (props, options) => {
    const { element } = options;
    element.renderRoot = element;
    return ComponentType(props, options);
  };
}

const customShadowlessElement = (tagName, props = {}, Component = (props, options) => {}, ...rest) =>
  compose(register(tagName, props), withNoShadowDOM, withSolid, ...rest)(Component);

const handleAccesibility = (slider, entry) => {
  entry.target.ariaHidden = !entry.isIntersecting;
};

const handleIntersection = (slider) => (entries, observer) => {
  entries.forEach((entry) => {
    handleAccesibility(slider, entry);
  });
};

function getBreakpointConfig(blazeConfig) {
  const config = {};
  for (const media in blazeConfig) {
    // if the media matches, override the config with media config
    if (window.matchMedia(media).matches) {
      const mediaConfig = blazeConfig[media];
      for (const key in mediaConfig) {
        // @ts-expect-error
        config[key] = mediaConfig[key];
      }
    }
  }

  return config;
}
class CustomBlazeSlider extends BlazeSlider {
  constructor(element, config) {
    super(element, config);

    this.trueConfig = getBreakpointConfig(config);
    this.wentForward = false;
    this.wentBackward = false;

    this.__initButtons();
    this.__fixSlider();
    this.__addAttributesToSlides();

    function handleKeyboardNav() {
      let prevIndex = 0,
        rootTarget = null;

      function internal(event) {
        const code = event.keyCode || event.which;

        if (code !== 9 || (!event.shiftKey && code !== 9)) return;

        const target = event.target.closest('.blaze-track > *:focus-within');
        if (!target || target === rootTarget) return;
        rootTarget = target;
        const index = parseInt(target.getAttribute('data-index'));
        if (index - this.config.slidesToScroll < 0) return (prevIndex = index);
        if (index % this.config.slidesToScroll !== 0) return (prevIndex = index);

        if (prevIndex < index) this.next();
        else this.prev();
        prevIndex = index;
      }

      return internal;
    }

    this.el.addEventListener('keyup', handleKeyboardNav().bind(this));
  }

  __addAttributesToSlides() {
    Array.from(this.slides).forEach((slide, index) => {
      slide.setAttribute('data-index', index);
    });
  }

  __initButtons() {
    const prevBtn = this.el.querySelector('button.blaze-prev');
    const nextBtn = this.el.querySelector('button.blaze-next');

    this.__buttons = {
      prev: prevBtn
        ? {
            ...(this?.__buttons?.prev || {}),
            element: prevBtn,
            initialDisplay: this?.__buttons?.prev?.initialDisplay || getComputedStyle(prevBtn).display,
          }
        : null,
      next: prevBtn
        ? {
            ...(this?.__buttons?.next || {}),
            element: nextBtn,
            initialDisplay: this?.__buttons?.next?.initialDisplay || getComputedStyle(nextBtn).display,
          }
        : null,
    };

    if (prevBtn) {
      if (this.totalSlides <= this.trueConfig.slidesToShow) {
        prevBtn.style.display = 'none';
      } else {
        prevBtn.style.display = this?.__buttons?.prev?.initialDisplay;
      }
    }

    if (nextBtn) {
      if (this.totalSlides <= this.trueConfig.slidesToShow) {
        nextBtn.style.display = 'none';
      } else {
        nextBtn.style.display = this?.__buttons?.next?.initialDisplay;
      }
    }

    this.__updateNavigation();
  }

  __fixSlider() {
    this.el.style.setProperty('--slides-to-show', this.trueConfig.slidesToShow);
  }

  __updateNavigation() {
    const state = this.states[this.stateIndex];
    if (this.__buttons.prev) {
      this.__buttons.prev.element.disabled = state.prev.moveSlides === 0;
    }
    if (this.__buttons.next) {
      this.__buttons.next.element.disabled = state.next.moveSlides === 0;
    }
  }

  refresh() {
    this.totalSlides = this.slides.length;
    this.trueConfig = getBreakpointConfig(this.passedConfig);
    super.refresh();
    this.wentForward = this.prevStateIndex < this.stateIndex;
    this.wentBackward = this.prevStateIndex > this.stateIndex;
    this.__initButtons();
    this.__fixSlider();
    this.__addAttributesToSlides();
  }

  onSlide(cb) {
    if (!cb) return;
    function handleSlide(page, first, last) {
      cb(page, first, last);
    }
    super.onSlide(handleSlide.bind(this));
  }

  set stateIndex(value) {
    this.prevStateIndex = this.__stateIndex;
    this.__stateIndex = value;
  }

  get stateIndex() {
    return this.__stateIndex;
  }

  prev(count) {
    this.wentForward = false;
    this.wentBackward = true;
    super.prev(count);
    this.__updateNavigation();
  }

  next(count) {
    this.wentForward = true;
    this.wentBackward = false;
    super.next(count);
    this.__updateNavigation();
  }
}

function BlazeSliderComponent(props, { element }) {
  const slider = createMemo(() => {
    const slider = new CustomBlazeSlider(element, props.config);
    return slider;
  });

  const observer = observable(slider);

  element.subscribe = (cb) => {
    return observer.subscribe(cb).unsubscribe;
  };

  createEffect(
    on(slider, (slider) => {
      if (!slider) return;
      if (props.usesObserver) {
        const observer = new IntersectionObserver(handleIntersection(slider), { root: element });

        Array.from(slider.slides).forEach((elem) => observer.observe(elem));

        onCleanup(observer.disconnect);
      }

      onCleanup(() => {
        slider.destroy();
      });
    })
  );

  createEffect(
    on(slider, (slider) => {
      if (!slider || !slider?.el?.id) return;
      function onLoaded() {
        element.dispatchEvent(
          new CustomEvent(`blaze-slider.loaded.${slider.el.id}`, { detail: { slider }, bubbles: true })
        );
      }

      window.addEventListener('load', onLoaded);

      return onCleanup(() => {
        window.removeEventListener('load', onLoaded);
      });
    })
  );
}

customShadowlessElement(
  'blaze-slider',
  { config: `{}`, totalSlides: Infinity, usesObserver: true },
  BlazeSliderComponent
);
